import React, { useEffect, useRef, useCallback } from "react";
import { X, Clock, Tag, ArrowRight, Check } from "lucide-react";
import SERVICE_DETAILS from "../lib/serviceDetails";
import { BorderOrnament, Paisley } from "./IndianMotifs";

export default function ServiceDetailModal({ service, onClose, onBook }) {
  const modalRef = useRef(null);
  const previousFocus = useRef(null);

  // WCAG 2.4.3: Focus management — trap focus & return focus on close
  useEffect(() => {
    if (service) {
      previousFocus.current = document.activeElement;
      document.documentElement.style.overflow = "hidden";
      // Focus the modal after a short delay for render
      setTimeout(() => modalRef.current?.focus(), 50);
    } else {
      document.documentElement.style.overflow = "";
      // Return focus to the element that opened the modal
      if (previousFocus.current && typeof previousFocus.current.focus === "function") {
        previousFocus.current.focus();
      }
    }
    return () => { document.documentElement.style.overflow = ""; };
  }, [service]);

  // WCAG: Focus trap — keep Tab cycling within the modal
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") { onClose(); return; }
    if (e.key !== "Tab") return;

    const focusable = modalRef.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable || focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      last.focus(); e.preventDefault();
    } else if (!e.shiftKey && document.activeElement === last) {
      first.focus(); e.preventDefault();
    }
  }, [onClose]);

  if (!service) return null;
  const d = SERVICE_DETAILS[service.id] || {};

  return (
    <div
      className="fixed inset-0 z-[100] grid place-items-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="service-detail-title"
      data-testid="service-detail-modal"
      onKeyDown={handleKeyDown}
    >
      <div className="absolute inset-0 bg-sage-900/45 backdrop-blur-md" onClick={onClose} aria-hidden="true" />

      <div
        ref={modalRef}
        tabIndex={-1}
        className="relative w-full max-w-3xl bg-cream rounded-[28px] ring-gold-soft overflow-hidden max-h-[90vh] flex flex-col outline-none"
      >
        <Paisley size={150} className="absolute -top-4 -right-4 text-gold-500/15" aria-hidden="true" />

        <button
          onClick={onClose}
          data-testid="service-detail-close"
          className="absolute top-5 right-5 z-10 w-10 h-10 grid place-items-center rounded-full bg-white text-sage-900 border border-sage-700/10"
          aria-label="Close dialog"
        >
          <X className="w-4 h-4" aria-hidden="true" />
        </button>

        <div className="overflow-y-auto px-7 md:px-12 py-10 md:py-12">
          <div className="text-[11px] uppercase tracking-[0.32em] text-sage-700/80 mb-3 flex items-center gap-3">
            <Tag className="w-3.5 h-3.5" aria-hidden="true" /> {d.tag || "Service"}
          </div>
          <h3 id="service-detail-title" className="font-display text-4xl md:text-5xl leading-[1.02] tracking-tightest text-ink">
            {service.name}
          </h3>
          <div className="mt-4 text-gold-600/70" aria-hidden="true"><BorderOrnament width={180} /></div>

          <p className="mt-5 text-ink/75 leading-relaxed max-w-2xl">{d.intro || service.desc}</p>

          <div className="mt-7 grid grid-cols-2 gap-4 max-w-md">
            {d.duration && (
              <div className="rounded-2xl bg-white/70 border border-sage-700/10 p-4">
                <div className="text-[10px] uppercase tracking-[0.28em] text-sage-700/70 mb-1 flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5" aria-hidden="true" /> Time
                </div>
                <div className="font-display text-lg text-sage-900">{d.duration}</div>
              </div>
            )}
            {d.price && (
              <div className="rounded-2xl bg-white/70 border border-sage-700/10 p-4">
                <div className="text-[10px] uppercase tracking-[0.28em] text-sage-700/70 mb-1">Starting</div>
                <div className="font-display text-lg text-sage-900">{d.price}</div>
              </div>
            )}
          </div>

          {d.includes && (
            <div className="mt-8">
              <div className="text-[11px] uppercase tracking-[0.32em] text-sage-700/80 mb-4">What's included</div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {d.includes.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-ink/75">
                    <span className="mt-1 grid place-items-center w-5 h-5 rounded-full bg-sage-700/10 text-sage-700 shrink-0" aria-hidden="true">
                      <Check className="w-3 h-3" strokeWidth={2.4} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {d.aftercare && (
            <div className="mt-8 rounded-2xl bg-sage-700/[0.06] border border-sage-700/10 p-5">
              <div className="text-[11px] uppercase tracking-[0.32em] text-sage-700/80 mb-2">Aftercare</div>
              <p className="text-sm text-ink/75 leading-relaxed">{d.aftercare}</p>
            </div>
          )}

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => { onClose(); onBook(service.name); }}
              data-testid="service-detail-book"
              data-cursor="hover"
              className="inline-flex items-center justify-center gap-3 rounded-full bg-sage-800 text-cream px-7 py-3.5 uppercase tracking-[0.24em] text-xs btn-glow"
            >
              Book this <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </button>
            <a
              href={`https://wa.me/15551234567?text=Hi%20Dentist%2C%20I%27d%20like%20to%20know%20more%20about%20${encodeURIComponent(service.name)}.`}
              target="_blank" rel="noreferrer"
              data-cursor="hover"
              className="inline-flex items-center justify-center rounded-full border border-sage-800/30 text-sage-900 px-7 py-3.5 uppercase tracking-[0.24em] text-xs"
            >
              Ask on WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
